import { useState } from "react";
import { Download, ExternalLink, Eye, FileText, Link2, Trash2, X } from "lucide-react";

import { ProgramMaterial } from "../../../api/program";

const formatFileSize = (size?: number) => {
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const cp1251SpecialChars =
  "\u0402\u0403\u201A\u0453\u201E\u2026\u2020\u2021\u20AC\u2030\u0409\u2039\u040A\u040C\u040B\u040F" +
  "\u0452\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u0000\u2122\u0459\u203A\u045A\u045C\u045B\u045F" +
  "\u00A0\u040E\u045E\u0408\u00A4\u0490\u00A6\u00A7\u0401\u00A9\u0404\u00AB\u00AC\u00AD\u00AE\u0407" +
  "\u00B0\u00B1\u0406\u0456\u0491\u00B5\u00B6\u00B7\u0451\u2116\u0454\u00BB\u0458\u0405\u0455\u0457";

const getCp1251Byte = (char: string) => {
  const code = char.charCodeAt(0);
  if (code <= 0xff) return code;
  if (code >= 0x0410 && code <= 0x044f) return code - 0x0410 + 0xc0;

  const specialIndex = cp1251SpecialChars.indexOf(char);
  return specialIndex >= 0 ? specialIndex + 0x80 : null;
};

const fixMojibakeFileName = (fileName: string) => {
  if (!/[ÐÑР]/.test(fileName)) return fileName;

  const bytes: number[] = [];
  for (const char of fileName) {
    const byte = getCp1251Byte(char);
    if (byte === null) return fileName;
    bytes.push(byte);
  }

  try {
    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(
      Uint8Array.from(bytes)
    );
    return decoded || fileName;
  } catch {
    return fileName;
  }
};

const fileExtension = (name: string) =>
  name.split(".").pop()?.toLowerCase() || "";

const inlineFileUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("dropbox.com")) {
      parsed.searchParams.delete("dl");
      parsed.searchParams.set("raw", "1");
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

const inlinePdfUrl = (url: string) => {
  const source = inlineFileUrl(url);
  return `${source.split("#")[0]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
};

const officeFileUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("dropbox.com")) {
      parsed.hostname = "dl.dropboxusercontent.com";
      parsed.searchParams.delete("dl");
      parsed.searchParams.delete("raw");
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

const getPreviewKind = (material: ProgramMaterial, title: string) => {
  const extension = fileExtension(title || material.name || material.url);
  const mimeType = material.mimeType || "";

  if (
    mimeType.startsWith("image/") ||
    ["jpg", "jpeg", "png", "webp", "gif"].includes(extension)
  ) {
    return "image";
  }

  if (mimeType === "application/pdf" || extension === "pdf") {
    return "pdf";
  }

  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(extension)) {
    return "office";
  }

  if (mimeType.startsWith("text/") || extension === "txt") {
    return "text";
  }

  return null;
};

type PreviewState = {
  material: ProgramMaterial;
  title: string;
  kind: "image" | "pdf" | "office" | "text";
};

const ProgramMaterials = ({
  materials = [],
  title = "Матеріали",
  emptyText,
  onDelete,
  inlineSinglePreview = false,
}: {
  materials?: ProgramMaterial[];
  title?: string;
  emptyText?: string;
  onDelete?: (materialId: string) => void;
  inlineSinglePreview?: boolean;
}) => {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [showOfficeHint, setShowOfficeHint] = useState(true);

  if (!materials.length && !emptyText) return null;

  const singleMaterial = materials.length === 1 ? materials[0] : null;
  const singleTitle = singleMaterial
    ? fixMojibakeFileName(singleMaterial.title || singleMaterial.name || "Матеріал")
    : "";
  const singlePreviewKind =
    inlineSinglePreview && singleMaterial && singleMaterial.kind !== "link"
      ? getPreviewKind(singleMaterial, singleTitle)
      : null;
  const singlePreviewUrl = singleMaterial
    ? inlineFileUrl(singleMaterial.url)
    : "";
  const singlePdfPreviewUrl = singleMaterial
    ? inlinePdfUrl(singleMaterial.url)
    : "";
  const singleOfficeSourceUrl = singleMaterial
    ? officeFileUrl(singleMaterial.url)
    : "";
  const singleOfficePreviewUrl =
    singlePreviewKind === "office"
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          singleOfficeSourceUrl
        )}`
      : "";
  const previewUrl = preview ? inlineFileUrl(preview.material.url) : "";
  const pdfPreviewUrl = preview ? inlinePdfUrl(preview.material.url) : "";
  const officeSourceUrl = preview ? officeFileUrl(preview.material.url) : "";
  const officePreviewUrl =
    preview?.kind === "office"
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          officeSourceUrl
        )}`
      : "";

  return (
    <>
      <div className="rounded-3xl border border-[#E8E1D8] bg-[#fffdf7] p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6f00]">
            {title}
          </p>
          <span className="rounded-full border border-[#E8E1D8] bg-white px-3 py-1 text-[11px] font-black text-[#625C55]">
            {materials.length}
          </span>
        </div>
        <div className="mt-4 grid gap-2">
          {materials.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#DCD7CF] bg-white px-4 py-5 text-sm font-semibold text-[#817A72]">
              {emptyText}
            </div>
          ) : (
            materials.map((material) => {
              const materialKind = material.kind || "file";
              const Icon = materialKind === "file" ? FileText : Link2;
              const materialTitle = fixMojibakeFileName(
                material.title || material.name || "Матеріал"
              );
              const previewKind =
                materialKind === "file"
                  ? getPreviewKind(material, materialTitle)
                  : null;

              return (
                <div
                  key={material._id}
                  className="flex min-w-0 items-center gap-3 rounded-2xl border border-[#E8E1D8] bg-white p-3"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#ffd400] text-black">
                    <Icon size={18} />
                  </span>
                  {previewKind ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowOfficeHint(true);
                        setPreview({
                          material,
                          title: materialTitle,
                          kind: previewKind,
                        });
                      }}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="block truncate text-sm font-black text-black">
                        {materialTitle}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#817A72]">
                        Швидкий перегляд
                        {formatFileSize(material.size)
                          ? ` · ${formatFileSize(material.size)}`
                          : ""}
                        <Eye size={12} />
                      </span>
                    </button>
                  ) : (
                    <a
                      href={material.url}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 flex-1"
                    >
                      <span className="block truncate text-sm font-black text-black">
                        {materialTitle}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#817A72]">
                        {materialKind === "file" ? "Файл" : "Посилання"}
                        {materialKind === "file" && formatFileSize(material.size)
                          ? ` · ${formatFileSize(material.size)}`
                          : ""}
                        <ExternalLink size={12} />
                      </span>
                    </a>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(material._id)}
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[#F2D3CD] bg-[#FFF6F4] text-[#9D3D2E]"
                      aria-label="Видалити матеріал"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {singleMaterial && singlePreviewKind && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-[#E8E1D8] bg-white">
            {singlePreviewKind === "image" && (
              <img
                src={singlePreviewUrl}
                alt={singleTitle}
                className="mx-auto max-h-[620px] w-full object-contain"
              />
            )}

            {singlePreviewKind === "pdf" && (
              <iframe
                title={singleTitle}
                src={singlePdfPreviewUrl}
                className="h-[78dvh] min-h-[680px] w-full border-0 bg-white"
              />
            )}

            {singlePreviewKind === "text" && (
              <iframe
                title={singleTitle}
                src={singlePreviewUrl}
                className="h-[72dvh] min-h-[560px] w-full border-0 bg-white"
              />
            )}

            {singlePreviewKind === "office" && (
              <div className="bg-[#111] p-3">
                <iframe
                  title={singleTitle}
                  src={singleOfficePreviewUrl}
                  className="h-[72dvh] min-h-[560px] w-full rounded-xl border-0 bg-white"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-5"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            onClick={() => setPreview(null)}
            aria-label="Закрити перегляд"
          />
          <div className="relative flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400] sm:max-h-[92vh] sm:rounded-[26px]">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b-2 border-black bg-[#fffdf2] px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                  Швидкий перегляд
                </p>
                <h3 className="mt-1 truncate text-lg font-black text-black sm:text-xl">
                  {preview.title}
                </h3>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={preview.material.url}
                  target="_blank"
                  rel="noreferrer"
                  className="grid h-10 w-10 place-items-center rounded-xl border-2 border-black bg-white text-black transition hover:bg-[#ffd400]"
                  aria-label="Відкрити файл"
                >
                  <Download size={18} />
                </a>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="grid h-10 w-10 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
                  aria-label="Закрити"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#111] p-3 sm:p-5">
              {preview.kind === "image" && (
                <img
                  src={previewUrl}
                  alt={preview.title}
                  className="mx-auto max-h-[78dvh] max-w-full rounded-2xl object-contain"
                />
              )}

              {preview.kind === "pdf" && (
                <iframe
                  title={preview.title}
                  src={pdfPreviewUrl}
                  className="h-[78dvh] w-full rounded-2xl border-0 bg-white"
                />
              )}

              {preview.kind === "text" && (
                <iframe
                  title={preview.title}
                  src={previewUrl}
                  className="h-[78dvh] w-full rounded-2xl border-0 bg-white"
                />
              )}

              {preview.kind === "office" && (
                <div className="space-y-3">
                  {showOfficeHint && (
                    <div className="flex items-start justify-between gap-3 rounded-2xl border border-[#3A3A3A] bg-[#1c1c1c] px-4 py-3 text-sm font-semibold text-white/80">
                      <span>
                        Якщо документ не відкрився у перегляді, натисни кнопку
                        завантаження вгорі праворуч.
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowOfficeHint(false)}
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-lg border border-white/20 text-white/80 transition hover:bg-white hover:text-black"
                        aria-label="Прибрати підказку"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  <iframe
                    title={preview.title}
                    src={officePreviewUrl}
                    className="h-[72dvh] w-full rounded-2xl border-0 bg-white"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProgramMaterials;
